// ============================================
// DOCUMENTS PAGE - i18n
// ============================================

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { 
  FolderOpen, 
  Trash2, 
  Download, 
  Upload, 
  Loader2, 
  Edit2,
  MoreVertical,
  FolderInput,
  AlertTriangle,
  Calendar,
  FileText,
  Image,
  File
} from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import * as Dialog from '@radix-ui/react-dialog';
import { 
  useMyDocuments, 
  useDeleteDocument, 
  useDownloadDocument,
  useMoveDocument,
  useDocumentFolders 
} from '@/hooks';
import { DocumentUploadModal, DocumentEditModal, FolderManager } from '@/components';
import { formatDate, formatFileSize, getFileExtension, cn, daysUntilExpiry } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/profile/documents')({
  component: DocumentsPage,
});

function DocumentsPage() {
  const { t } = useTranslation(['panel', 'common', 'enums']);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState<any>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [moveTarget, setMoveTarget] = useState<{ docId: string; showModal: boolean } | null>(null);

  const DOCUMENT_TYPE_LABELS: Record<string, string> = {
    PASSPORT: t('documents.types.PASSPORT'),
    CDC: t('documents.types.CDC'),
    CERTIFICATE: t('documents.types.CERTIFICATE'),
    DIPLOMA: t('documents.types.DIPLOMA'),
    MEDICAL: t('documents.types.MEDICAL'),
    FLAG_DOCUMENT: t('documents.types.FLAG_DOCUMENT'),
    CONTRACT: t('documents.types.CONTRACT'),
    OTHER: t('documents.types.OTHER'),
  };

  const { data: docsResponse, isLoading } = useMyDocuments();
  const { data: foldersResponse } = useDocumentFolders();
  const deleteMutation = useDeleteDocument();
  const downloadMutation = useDownloadDocument();
  const moveMutation = useMoveDocument();

  const allDocuments: any[] = (docsResponse as any)?.data || docsResponse || [];
  const folders: any[] = (foldersResponse as any)?.data || foldersResponse || [];

  const documents = selectedFolderId
    ? allDocuments.filter((doc) => doc.folderId === selectedFolderId)
    : allDocuments;

  const handleDelete = (id: string) => {
    if (confirm(t('documents.confirm.delete'))) {
      deleteMutation.mutate(id);
    }
  };

  const handleDownload = async (doc: any) => {
    let fileUrl = doc.fileUrl || doc.url;
    
    if (!fileUrl) {
      alert(t('documents.alert.fileNotFound'));
      return;
    }

    // data: URI ise (base64) — doğrudan blob olarak indir
    if (fileUrl.startsWith('data:')) {
      try {
        const res = await fetch(fileUrl);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.name || 'belge';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch {
        alert(t('documents.alert.downloadError'));
      }
      return;
    }
    
    // Göreceli URL ise API base URL ekle
    if (!fileUrl.startsWith('http')) {
      const apiBase = import.meta.env.VITE_API_URL || 'https://api.crewatsea.com/api/v1';
      // /uploads/ ile başlıyorsa API base'den /api/v1 kısmını kaldır
      if (fileUrl.startsWith('/uploads/')) {
        const baseUrl = apiBase.replace(/\/api\/v1\/?$/, '');
        fileUrl = `${baseUrl}${fileUrl}`;
      } else {
        fileUrl = `${apiBase}/${fileUrl}`;
      }
    }
    
    window.open(fileUrl, '_blank');
  };

  const handleMoveDocument = async (docId: string, folderId: string | null) => {
    try {
      await moveMutation.mutateAsync({ id: docId, folderId });
      setMoveTarget(null);
    } catch (err: any) {
      alert(err?.message || t('documents.alert.moveError'));
    }
  };

  const getFileIcon = (mimeType: string, fileName: string) => {
    if (mimeType?.includes('pdf')) return <FileText className="w-6 h-6 text-red-500" />;
    if (mimeType?.includes('image')) return <Image className="w-6 h-6 text-green-500" />;
    if (mimeType?.includes('word') || fileName?.endsWith('.docx')) return <FileText className="w-6 h-6 text-blue-500" />;
    return <File className="w-6 h-6 text-gray-500" />;
  };

  const getExpiryStatus = (expiryDate: string | null) => {
    if (!expiryDate) return null;
    const days = daysUntilExpiry(expiryDate);
    if (days < 0) return { status: 'expired', label: t('documents.expiry.expired'), color: 'text-red-600 bg-red-50' };
    if (days <= 30) return { status: 'warning', label: t('documents.expiry.days', { count: days }), color: 'text-amber-600 bg-amber-50' };
    if (days <= 90) return { status: 'soon', label: t('documents.expiry.days', { count: days }), color: 'text-yellow-600 bg-yellow-50' };
    return null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-maritime-600" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">{t('documents.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {t('documents.uploadedCount', { count: allDocuments.length })}
            {selectedFolderId && ` • ${t('documents.showingCount', { count: documents.length })}`}
          </p>
        </div>
        <button 
          className="btn-primary text-sm"
          onClick={() => setShowUploadModal(true)}
        >
          <Upload className="w-4 h-4" />
          <span className="hidden sm:inline">{t('documents.uploadDocument')}</span>
          <span className="sm:hidden">Yükle</span>
        </button>
      </div>

      {/* Klasörler - Mobilde yatay scroll, desktop'ta sidebar */}
      <div className="mb-4 md:hidden">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={() => setSelectedFolderId(null)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
              !selectedFolderId
                ? 'bg-maritime-600 text-white'
                : 'bg-gray-100 text-gray-700'
            )}
          >
            <FolderOpen className="w-3.5 h-3.5" />
            {t('documents.allDocuments', { defaultValue: 'Tümü' })}
          </button>
          {folders.map((folder: any) => (
            <button
              key={folder.id}
              onClick={() => setSelectedFolderId(folder.id)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
                selectedFolderId === folder.id
                  ? 'bg-maritime-600 text-white'
                  : 'bg-gray-100 text-gray-700'
              )}
            >
              <FolderOpen className="w-3.5 h-3.5" />
              {folder.name}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-6">
        {/* Sidebar - Sadece desktop */}
        <div className="hidden md:block w-64 flex-shrink-0">
          <div className="card p-4">
            <FolderManager
              selectedFolderId={selectedFolderId}
              onFolderSelect={setSelectedFolderId}
            />
          </div>
        </div>

        {/* Main Content - Documents */}
        <div className="flex-1 min-w-0">
          {documents.length === 0 ? (
            <div className="card p-8 md:p-12 text-center">
              <FolderOpen className="w-12 h-12 md:w-16 md:h-16 mx-auto text-gray-300 mb-3" />
              <h3 className="text-base md:text-lg font-medium text-gray-900 mb-2">
                {selectedFolderId ? t('documents.empty.folderEmpty') : t('documents.empty.title')}
              </h3>
              <p className="text-gray-500 mb-4">{t('documents.empty.description')}</p>
              <button 
                className="btn-primary"
                onClick={() => setShowUploadModal(true)}
              >
                <Upload className="w-4 h-4" />
                {selectedFolderId ? t('documents.uploadDocument') : t('documents.empty.addFirst')}
              </button>
            </div>
          ) : (
            <div className="card divide-y divide-gray-100">
              {documents.map((doc: any) => {
                const expiryInfo = getExpiryStatus(doc.expiryDate);
                
                return (
                  <div key={doc.id} className="p-3 md:p-4 hover:bg-gray-50 group">
                    <div className="flex items-start gap-3">
                      {/* File Icon */}
                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-maritime bg-gray-100 flex items-center justify-center flex-shrink-0">
                        {getFileIcon(doc.mimeType, doc.fileName || doc.name)}
                      </div>

                      {/* File Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate text-sm md:text-base">{doc.title || doc.name}</h3>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          <span className="badge badge-gray text-xs">
                            {DOCUMENT_TYPE_LABELS[doc.category] || DOCUMENT_TYPE_LABELS[doc.type] || doc.category || doc.type}
                          </span>
                          {expiryInfo && (
                            <span className={cn('badge text-xs', expiryInfo.color)}>
                              {expiryInfo.status === 'expired' ? (
                                <AlertTriangle className="w-3 h-3 mr-1" />
                              ) : (
                                <Calendar className="w-3 h-3 mr-1" />
                              )}
                              {expiryInfo.label}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                          <span>{getFileExtension(doc.fileName || doc.title || doc.name || '').toUpperCase() || 'DOC'}</span>
                          {doc.fileSize && (
                            <>
                              <span>•</span>
                              <span>{formatFileSize(doc.fileSize)}</span>
                            </>
                          )}
                          <span>•</span>
                          <span>{formatDate(doc.createdAt)}</span>
                        </div>
                      </div>

                      {/* Actions - mobilde her zaman görünür */}
                      <div className="flex items-center gap-0.5 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <button
                          onClick={() => handleDownload(doc)}
                          className="p-2 text-gray-400 hover:text-maritime-600 hover:bg-maritime-50 rounded-maritime"
                          title={t('common:download')}
                          disabled={downloadMutation.isPending}
                        >
                          {downloadMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </button>
                        
                        {/* More Actions */}
                        <Popover.Root>
                          <Popover.Trigger asChild>
                            <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-maritime">
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </Popover.Trigger>
                          <Popover.Portal>
                            <Popover.Content
                              className="bg-white rounded-maritime shadow-lg border border-gray-200 py-1 z-50 min-w-[160px]"
                              sideOffset={5}
                              align="end"
                            >
                              <button
                                onClick={() => setEditingDocument(doc)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <Edit2 className="w-4 h-4" />
                                {t('common:edit')}
                              </button>
                              <button
                                onClick={() => setMoveTarget({ docId: doc.id, showModal: true })}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <FolderInput className="w-4 h-4" />
                                {t('documents.moveToFolder')}
                              </button>
                              <button
                                onClick={() => handleDelete(doc.id)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                                disabled={deleteMutation.isPending}
                              >
                                <Trash2 className="w-4 h-4" />
                                {t('common:delete')}
                              </button>
                            </Popover.Content>
                          </Popover.Portal>
                        </Popover.Root>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      <DocumentUploadModal
        open={showUploadModal}
        onOpenChange={setShowUploadModal}
        onSuccess={() => {}}
      />

      {/* Edit Modal */}
      {editingDocument && (
        <DocumentEditModal
          open={!!editingDocument}
          onOpenChange={(open) => !open && setEditingDocument(null)}
          document={editingDocument}
          onSuccess={() => setEditingDocument(null)}
        />
      )}

      {/* Move to Folder Modal */}
      <Dialog.Root 
        open={moveTarget?.showModal || false} 
        onOpenChange={(open) => !open && setMoveTarget(null)}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-maritime shadow-lg w-full max-w-sm z-50">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                {t('documents.moveToFolder')}
              </Dialog.Title>
              <Dialog.Close className="p-1 rounded-maritime hover:bg-gray-100">
                <span className="sr-only">{t('common:close')}</span>
                ×
              </Dialog.Close>
            </div>
            <Dialog.Description className="sr-only">
              {t('documents.moveDescription')}
            </Dialog.Description>
            <div className="p-4 space-y-2">
              <button
                onClick={() => moveTarget && handleMoveDocument(moveTarget.docId, null)}
                className="w-full flex items-center gap-2 p-3 rounded-maritime hover:bg-gray-100 text-left"
              >
                <FolderOpen className="w-5 h-5 text-gray-400" />
                <span>{t('documents.rootFolder')}</span>
              </button>
              {folders.map((folder) => (
                <button
                  key={folder.id}
                  onClick={() => moveTarget && handleMoveDocument(moveTarget.docId, folder.id)}
                  className="w-full flex items-center gap-2 p-3 rounded-maritime hover:bg-gray-100 text-left"
                >
                  <FolderOpen 
                    className="w-5 h-5" 
                    style={{ color: folder.color || '#6B7280' }} 
                  />
                  <span>{folder.name}</span>
                </button>
              ))}
              {moveMutation.isPending && (
                <div className="flex items-center justify-center py-2">
                  <Loader2 className="w-5 h-5 animate-spin text-maritime-600" />
                </div>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
